import { data } from 'react-router';
import { store } from '@/redux/store';
import { shouldSimulateLoaderError, shouldSimulateQueryErrorAfterLoad } from '@/utils/devErrorSimulation';

/**
 * Initiate an RTK Query endpoint from a route loader.
 * Pre-warms the RTK Query cache so components render with data immediately.
 *
 * @param {Object} api - The RTK Query API (e.g., inventoryItemsApi)
 * @param {string} endpointName - The endpoint name (e.g., 'getInventory')
 * @param {any} args - Arguments to pass to the endpoint
 * @returns {Promise<any>} The query result data
 * @throws {Response} Throws a data() response on error (caught by error boundary)
 */
export async function initiateQuery(api, endpointName, args) {
  // Dev-only: simulate loader errors for testing RouteErrorBoundary
  if (import.meta.env.DEV && shouldSimulateLoaderError()) {
    throw data('Internal Server Error', { status: 500 });
  }

  const endpoint = api.endpoints[endpointName];

  if (!endpoint) {
    throw new Error(`Unknown endpoint: ${endpointName}`);
  }

  const result = await store.dispatch(endpoint.initiate(args));

  if (result.error) {
    // Dev-only: if queryErrorAfterLoad is enabled, don't throw from loader
    // This allows the page to load, then component refetches will fail showing QueryErrorState
    if (import.meta.env.DEV && shouldSimulateQueryErrorAfterLoad()) {
      return { list: [], count: 0 };
    }

    const status = result.error.status || 500;
    const message =
      result.error.data?.message ||
      result.error.message ||
      'Failed to load data';

    // Throw to error boundary with HTTP status
    throw data(message, { status: typeof status === 'number' ? status : 500 });
  }

  return result.data;
}

/**
 * Get URL search params helper for loaders.
 *
 * @param {Request} request - The loader request object
 * @returns {URLSearchParams}
 */
export function getSearchParams(request) {
  return new URL(request.url).searchParams;
}
